import React, { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Wallet, 
  Plus, 
  PencilSimple, 
  Trash, 
  X, 
  Coins, 
  Receipt, 
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  Bank,
  ChartPie,
  Percent,
  ArrowUp,
  ArrowDown,
  TrendUp,
  TrendDown,
  CheckSquare
} from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import GeniLoader from "../components/GeniLoader";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

// Color palette for pie chart
const CHART_COLORS = [
  '#00E676', // Cash - Green
  '#FFC107', // Yellow
  '#2196F3', // Blue
  '#9C27B0', // Purple
  '#FF5722', // Orange
  '#00BCD4', // Cyan
  '#E91E63', // Pink
  '#4CAF50', // Green
  '#FF9800', // Amber
  '#607D8B', // Blue Grey
];

export default function Holdings() {
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  
  // Cash balance & allocation state
  const [availableCash, setAvailableCash] = useState(0);
  const [portfolioTotal, setPortfolioTotal] = useState(0);
  const [portfolioMarketValue, setPortfolioMarketValue] = useState(null);
  const [totalInvested, setTotalInvested] = useState(0);
  const [totalRealizedPnl, setTotalRealizedPnl] = useState(0);
  const [totalUnrealizedPnl, setTotalUnrealizedPnl] = useState(null);
  const [allocation, setAllocation] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [livePrices, setLivePrices] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Cash edit modal
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashInput, setCashInput] = useState("");
  const [savingCash, setSavingCash] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    transaction_type: "buy",
    asset: "",
    transaction_date: "",
    quantity: "",
    price_per_unit: "",
    fees: "",
    notes: "",
    add_proceeds_to_cash: true,
    deduct_from_cash: false
  });
  
  // Search/filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("transaction_date");
  const [sortDir, setSortDir] = useState("desc");
  
  // Delete confirmation
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchHoldings();
  }, []);

  const fetchHoldings = async () => {
    try {
      const response = await api.get('/holdings');
      setSummaries(response.data.summaries || []);
      setTransactions(response.data.transactions || []);
      setTotalAssets(response.data.total_assets || 0);
      setAvailableCash(response.data.available_cash || 0);
      setPortfolioTotal(response.data.portfolio_total || 0);
      setPortfolioMarketValue(response.data.portfolio_total_market_value || null);
      setTotalInvested(response.data.total_invested || 0);
      setTotalRealizedPnl(response.data.total_realized_pnl || 0);
      setTotalUnrealizedPnl(response.data.total_unrealized_pnl || null);
      setAllocation(response.data.allocation || []);
      setCurrency(response.data.currency || "USD");
      setLivePrices(response.data.live_prices || null);
    } catch (error) {
      if (error.code !== 'NO_AUTH_SESSION') {
        console.error("Error fetching holdings:", error);
        toast.error("Failed to load holdings");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: "buy",
      asset: "",
      transaction_date: "",
      quantity: "",
      price_per_unit: "",
      fees: "",
      notes: "",
      add_proceeds_to_cash: true,
      deduct_from_cash: false
    });
    setEditingTransaction(null);
  };

  const openAddModal = (type = "buy") => {
    // For SELL, check if user has any assets to sell
    if (type === 'sell' && sellableAssets.length === 0) {
      toast.error("You don't have any holdings to sell");
      return;
    }
    
    resetForm();
    setFormData(prev => ({
      ...prev,
      transaction_type: type,
      transaction_date: new Date().toISOString().split('T')[0],
      // Pre-select first sellable asset if opening SELL modal
      asset: type === 'sell' && sellableAssets.length > 0 ? sellableAssets[0].asset : ""
    }));
    setShowModal(true);
  };

  const openEditModal = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      transaction_type: transaction.transaction_type || "buy",
      asset: transaction.asset || "",
      transaction_date: transaction.transaction_date || "",
      quantity: transaction.quantity?.toString() || "",
      price_per_unit: transaction.price_per_unit?.toString() || "",
      fees: transaction.fees?.toString() || "0",
      notes: transaction.notes || "",
      add_proceeds_to_cash: transaction.add_proceeds_to_cash ?? true,
      deduct_from_cash: transaction.deduct_from_cash ?? false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Cash modal handlers
  const openCashModal = () => {
    setCashInput(availableCash.toString());
    setShowCashModal(true);
  };

  const closeCashModal = () => {
    setShowCashModal(false);
    setCashInput("");
  };

  const handleSaveCash = async () => {
    const newCash = parseFloat(cashInput);
    if (isNaN(newCash) || newCash < 0) {
      toast.error("Please enter a valid non-negative amount");
      return;
    }

    setSavingCash(true);
    try {
      await api.put('/holdings/settings', { available_cash: newCash });
      toast.success("Cash balance updated");
      closeCashModal();
      fetchHoldings();
    } catch (error) {
      console.error("Error updating cash:", error);
      toast.error(error.response?.data?.detail || "Failed to update cash balance");
    } finally {
      setSavingCash(false);
    }
  };

  // Get current quantity held for an asset (for sell validation)
  const getCurrentQuantity = (assetSymbol) => {
    const summary = summaries.find(s => s.asset === assetSymbol.toUpperCase());
    return summary?.current_quantity || 0;
  };

  // Get asset details for sell dropdown
  const getAssetDetails = (assetSymbol) => {
    return summaries.find(s => s.asset === assetSymbol.toUpperCase());
  };

  // Get list of sellable assets (current_quantity > 0)
  const sellableAssets = useMemo(() => {
    return summaries.filter(s => s.current_quantity > 0);
  }, [summaries]);

  // Check if user can sell (has any sellable assets)
  const canSell = sellableAssets.length > 0;

  // Get selected asset details for sell form
  const selectedSellAsset = useMemo(() => {
    if (formData.transaction_type !== 'sell' || !formData.asset) return null;
    return getAssetDetails(formData.asset);
  }, [formData.transaction_type, formData.asset, summaries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate asset
    if (!formData.asset.trim()) {
      toast.error("Asset symbol is required");
      return;
    }

    // For SELL: Additional validation
    if (formData.transaction_type === 'sell') {
      const currentQty = getCurrentQuantity(formData.asset);
      
      // Check if user actually holds this asset
      if (currentQty <= 0) {
        toast.error(`You do not hold any ${formData.asset.toUpperCase()} to sell`);
        return;
      }
    }

    if (!formData.transaction_date) {
      toast.error("Transaction date is required");
      return;
    }
    const qty = parseFloat(formData.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error("Quantity must be a positive number");
      return;
    }
    const price = parseFloat(formData.price_per_unit);
    if (isNaN(price) || price <= 0) {
      toast.error("Price per unit must be a positive number");
      return;
    }
    const fees = parseFloat(formData.fees) || 0;
    if (fees < 0) {
      toast.error("Fees cannot be negative");
      return;
    }

    // For sell, validate we have enough quantity
    if (formData.transaction_type === 'sell') {
      const currentQty = getCurrentQuantity(formData.asset);
      if (qty > currentQty) {
        toast.error(`Cannot sell ${qty} ${formData.asset.toUpperCase()}. You only hold ${currentQty.toFixed(8)}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        asset: formData.asset.trim().toUpperCase(),
        transaction_type: formData.transaction_type,
        transaction_date: formData.transaction_date,
        quantity: qty,
        price_per_unit: price,
        fees: fees,
        notes: formData.notes.trim(),
        add_proceeds_to_cash: formData.add_proceeds_to_cash,
        deduct_from_cash: formData.deduct_from_cash
      };

      if (editingTransaction) {
        // Update existing
        await api.put(`/holdings/transaction/${editingTransaction.id}`, payload);
        toast.success("Transaction updated");
      } else {
        // Create new
        await api.post('/holdings/transaction', payload);
        const typeLabel = formData.transaction_type === 'buy' ? 'Bought' : 'Sold';
        toast.success(`${typeLabel} ${qty} ${payload.asset}`);
      }

      closeModal();
      fetchHoldings();
    } catch (error) {
      console.error("Error saving transaction:", error);
      toast.error(error.response?.data?.detail || "Failed to save transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (transactionId) => {
    try {
      await api.delete(`/holdings/transaction/${transactionId}`);
      toast.success("Transaction deleted");
      setDeletingId(null);
      fetchHoldings();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error(error.response?.data?.detail || "Failed to delete transaction");
    }
  };

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let result = [...transactions];
    
    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.asset?.toLowerCase().includes(q) ||
        t.notes?.toLowerCase().includes(q) ||
        t.transaction_type?.toLowerCase().includes(q)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'transaction_date' || sortField === 'created_at') {
        aVal = aVal || '';
        bVal = bVal || '';
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        // numbers
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [transactions, searchQuery, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? 
      <CaretUp size={12} weight="fill" className="ml-1" /> : 
      <CaretDown size={12} weight="fill" className="ml-1" />;
  };

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#1a1a1a] border border-white/20 rounded px-3 py-2 shadow-lg">
          <p className="font-bold text-white">{data.label}</p>
          <p className="text-[#00E676] font-mono">
            ${data.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-white/60 text-sm">{data.percentage.toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="holdings-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-black font-heading tracking-tight flex items-center gap-3">
              <Wallet size={32} weight="duotone" className="text-[#00E676]" />
              Holdings
            </h2>
            <p className="text-white/60 mt-1">Track your long-term asset purchases, sales, and average hold price</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => openAddModal('buy')}
              data-testid="add-buy-btn"
              className="bg-[#00E676] text-black font-bold px-4 hover:bg-[#00E676]/90 gap-2"
            >
              <ArrowDown size={18} weight="bold" />
              Buy
            </Button>
            <Button
              onClick={() => openAddModal('sell')}
              disabled={!canSell}
              data-testid="add-sell-btn"
              className="bg-[#FF5722] text-white font-bold px-4 hover:bg-[#FF5722]/90 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canSell ? "No assets to sell" : ""}
            >
              <ArrowUp size={18} weight="bold" />
              Sell
            </Button>
          </div>
        </div>

        {/* Empty State */}
        {summaries.length === 0 && availableCash === 0 && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wallet size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">No Holdings Yet</h3>
            <p className="text-white/60 mb-6">
              Add your first asset purchase to start tracking your portfolio.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => openAddModal('buy')}
                className="bg-[#00E676] text-black font-bold px-6 hover:bg-[#00E676]/90 gap-2"
              >
                <ArrowDown size={18} weight="bold" />
                Add First Buy
              </Button>
              <Button
                onClick={openCashModal}
                variant="outline"
                className="bg-transparent border-white/20 text-white hover:bg-white/5 gap-2"
              >
                <Bank size={18} weight="duotone" />
                Set Cash Balance
              </Button>
            </div>
          </div>
        )}

        {/* Content when holdings or cash exist */}
        {(summaries.length > 0 || availableCash > 0) && (
          <>
            {/* Portfolio Overview - Top Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Portfolio Summary Cards */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {/* Total Portfolio */}
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs font-mono mb-1">
                      {portfolioMarketValue ? 'MARKET VALUE' : 'PORTFOLIO VALUE'}
                    </div>
                    <div className="text-2xl font-bold text-[#00E676]">
                      ${(portfolioMarketValue || portfolioTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {portfolioMarketValue && portfolioMarketValue !== portfolioTotal && (
                      <div className="text-xs text-white/40 mt-1 font-mono">
                        Cost: ${portfolioTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </div>
                  
                  {/* Available Cash */}
                  <div 
                    className="bg-[#121212] border border-white/10 rounded-sm p-4 cursor-pointer hover:border-white/20 transition-colors group"
                    onClick={openCashModal}
                    data-testid="cash-balance-card"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-white/40 text-xs font-mono mb-1">AVAILABLE CASH</div>
                      <PencilSimple size={14} className="text-white/30 group-hover:text-white/60" />
                    </div>
                    <div className="text-2xl font-bold text-[#FFC107]">
                      ${availableCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  
                  {/* Unrealized P&L - only show if we have live prices */}
                  {totalUnrealizedPnl !== null ? (
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-1">UNREALIZED P&L</div>
                      <div className={`text-2xl font-bold flex items-center gap-1 ${
                        totalUnrealizedPnl >= 0 ? 'text-[#00E676]' : 'text-[#FF5722]'
                      }`}>
                        {totalUnrealizedPnl >= 0 ? <TrendUp size={20} /> : <TrendDown size={20} />}
                        {totalUnrealizedPnl >= 0 ? '+' : ''}${Math.abs(totalUnrealizedPnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-xs text-white/40 mt-1 font-mono">
                        BTC/ETH only
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-1">COST BASIS</div>
                      <div className="text-2xl font-bold text-white">
                        ${totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                  
                  {/* Realized PnL */}
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs font-mono mb-1">REALIZED P&L</div>
                    <div className={`text-2xl font-bold flex items-center gap-1 ${
                      totalRealizedPnl >= 0 ? 'text-[#00E676]' : 'text-[#FF5722]'
                    }`}>
                      {totalRealizedPnl >= 0 ? <TrendUp size={20} /> : <TrendDown size={20} />}
                      {totalRealizedPnl >= 0 ? '+' : ''}${Math.abs(totalRealizedPnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                
                {/* Live prices indicator */}
                {livePrices && Object.keys(livePrices).length > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-white/40">
                    <div className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
                    <span>Live prices: BTC ${livePrices.BTC?.toLocaleString()}, ETH ${livePrices.ETH?.toLocaleString()}</span>
                  </div>
                )}
              </div>
              
              {/* Pie Chart */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ChartPie size={18} weight="duotone" className="text-[#00E676]" />
                  <span className="text-white/60 text-xs font-mono">ALLOCATION</span>
                </div>
                
                {allocation.length > 0 ? (
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={allocation}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="label"
                        >
                          {allocation.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={CHART_COLORS[index % CHART_COLORS.length]} 
                              stroke="transparent"
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[180px] flex items-center justify-center text-white/30 text-sm">
                    No allocation data
                  </div>
                )}
              </div>
            </div>

            {/* Allocation Breakdown */}
            {allocation.length > 0 && (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-8">
                <h3 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                  <Percent size={20} weight="duotone" className="text-[#FFC107]" />
                  Portfolio Allocation
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {allocation.map((item, index) => (
                    <div 
                      key={item.label}
                      className="bg-[#0A0A0A] border border-white/5 rounded-sm p-3"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <span className="font-bold text-sm">{item.label}</span>
                      </div>
                      <div className="text-lg font-mono font-semibold text-white">
                        {item.percentage.toFixed(1)}%
                      </div>
                      <div className="text-xs text-white/40 font-mono">
                        ${item.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Asset Summary Cards */}
            {summaries.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                  <Coins size={20} weight="duotone" className="text-[#FFC107]" />
                  Holdings by Asset
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {summaries.map((summary) => (
                    <div
                      key={summary.asset}
                      data-testid={`summary-card-${summary.asset}`}
                      className={`bg-[#121212] border rounded-sm p-5 hover:border-white/20 transition-colors ${
                        summary.current_quantity > 0 ? 'border-white/10' : 'border-white/5 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-xl font-black font-heading">{summary.asset}</div>
                        <div className="text-xs text-white/40 font-mono">
                          {summary.transaction_count} txn{summary.transaction_count !== 1 ? 's' : ''}
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-white/50 text-sm">Current Holding</span>
                          <span className={`font-mono font-semibold ${summary.current_quantity > 0 ? 'text-white' : 'text-white/40'}`}>
                            {summary.current_quantity.toLocaleString(undefined, { maximumFractionDigits: 8 })} {summary.asset}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/40">Bought / Sold</span>
                          <span className="font-mono text-white/60">
                            <span className="text-[#00E676]">{summary.total_bought_quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                            {' / '}
                            <span className="text-[#FF5722]">{summary.total_sold_quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                          </span>
                        </div>
                        
                        {summary.current_quantity > 0 && (
                          <>
                            {/* Live price and market value for BTC/ETH */}
                            {summary.has_live_price && summary.current_price && (
                              <div className="bg-[#0A0A0A] border border-[#00E676]/20 rounded-sm p-2 mb-2">
                                <div className="flex justify-between items-center text-xs mb-1">
                                  <span className="text-white/50">Current Price</span>
                                  <span className="font-mono text-white flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
                                    ${summary.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                  <span className="text-white/50">Market Value</span>
                                  <span className="font-mono text-[#00E676]">
                                    ${summary.current_market_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            <div className="flex justify-between items-center">
                              <span className="text-white/50 text-sm">Cost Basis</span>
                              <span className="font-mono font-semibold text-white/70">
                                ${summary.current_cost_basis.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            
                            <div className="flex justify-between items-center border-t border-white/10 pt-3">
                              <span className="text-white/50 text-sm">Avg Hold Price</span>
                              <span className="font-mono font-bold text-[#FFC107]">
                                ${summary.average_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            
                            {/* Unrealized P&L for BTC/ETH */}
                            {summary.has_live_price && summary.unrealized_pnl !== null && (
                              <div className="flex justify-between items-center">
                                <span className="text-white/50 text-sm">Unrealized P&L</span>
                                <span className={`font-mono font-semibold ${summary.unrealized_pnl >= 0 ? 'text-[#00E676]' : 'text-[#FF5722]'}`}>
                                  {summary.unrealized_pnl >= 0 ? '+' : ''}${summary.unrealized_pnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  {summary.unrealized_pnl_percentage !== null && (
                                    <span className="text-xs ml-1 opacity-70">
                                      ({summary.unrealized_pnl_percentage >= 0 ? '+' : ''}{summary.unrealized_pnl_percentage.toFixed(1)}%)
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                        
                        {summary.realized_pnl !== 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-white/50 text-sm">Realized P&L</span>
                            <span className={`font-mono font-semibold ${summary.realized_pnl >= 0 ? 'text-[#00E676]' : 'text-[#FF5722]'}`}>
                              {summary.realized_pnl >= 0 ? '+' : ''}${summary.realized_pnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        
                        {summary.last_transaction_date && (
                          <div className="flex justify-between items-center text-xs text-white/30">
                            <span>Last transaction</span>
                            <span className="font-mono">
                              {format(new Date(summary.last_transaction_date), "MMM d, yyyy")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transaction History */}
            {transactions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold font-heading flex items-center gap-2">
                    <Receipt size={20} weight="duotone" className="text-cyan-400" />
                    Transaction History
                  </h3>
                  
                  {/* Search */}
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-[#121212] border-white/10 text-white pl-9 w-48"
                      data-testid="search-input"
                    />
                  </div>
                </div>

                <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 text-left">
                          <th 
                            className="px-4 py-3 text-xs font-mono text-white/40 cursor-pointer hover:text-white/60"
                            onClick={() => toggleSort('transaction_date')}
                          >
                            <div className="flex items-center">
                              DATE <SortIcon field="transaction_date" />
                            </div>
                          </th>
                          <th className="px-4 py-3 text-xs font-mono text-white/40">TYPE</th>
                          <th 
                            className="px-4 py-3 text-xs font-mono text-white/40 cursor-pointer hover:text-white/60"
                            onClick={() => toggleSort('asset')}
                          >
                            <div className="flex items-center">
                              ASSET <SortIcon field="asset" />
                            </div>
                          </th>
                          <th 
                            className="px-4 py-3 text-xs font-mono text-white/40 cursor-pointer hover:text-white/60"
                            onClick={() => toggleSort('quantity')}
                          >
                            <div className="flex items-center">
                              QUANTITY <SortIcon field="quantity" />
                            </div>
                          </th>
                          <th 
                            className="px-4 py-3 text-xs font-mono text-white/40 cursor-pointer hover:text-white/60"
                            onClick={() => toggleSort('price_per_unit')}
                          >
                            <div className="flex items-center">
                              PRICE/UNIT <SortIcon field="price_per_unit" />
                            </div>
                          </th>
                          <th className="px-4 py-3 text-xs font-mono text-white/40">TOTAL VALUE</th>
                          <th className="px-4 py-3 text-xs font-mono text-white/40">FEES</th>
                          <th className="px-4 py-3 text-xs font-mono text-white/40">NOTES</th>
                          <th className="px-4 py-3 text-xs font-mono text-white/40 text-right">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="px-4 py-8 text-center text-white/40">
                              {searchQuery ? "No transactions match your search" : "No transactions yet"}
                            </td>
                          </tr>
                        ) : (
                          filteredTransactions.map((t) => {
                            const totalValue = t.quantity * t.price_per_unit;
                            const isBuy = t.transaction_type === 'buy';
                            return (
                              <tr 
                                key={t.id} 
                                data-testid={`transaction-row-${t.id}`}
                                className="border-b border-white/5 hover:bg-white/5"
                              >
                                <td className="px-4 py-3 font-mono text-sm text-white/70">
                                  {t.transaction_date ? format(new Date(t.transaction_date), "MMM d, yyyy") : "—"}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                                    isBuy 
                                      ? 'bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30' 
                                      : 'bg-[#FF5722]/10 text-[#FF5722] border border-[#FF5722]/30'
                                  }`}>
                                    {isBuy ? <ArrowDown size={12} weight="bold" /> : <ArrowUp size={12} weight="bold" />}
                                    {isBuy ? 'BUY' : 'SELL'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-bold">{t.asset}</span>
                                </td>
                                <td className="px-4 py-3 font-mono text-sm">
                                  {t.quantity?.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                                </td>
                                <td className="px-4 py-3 font-mono text-sm">
                                  ${t.price_per_unit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className={`px-4 py-3 font-mono text-sm ${isBuy ? 'text-[#00E676]' : 'text-[#FF5722]'}`}>
                                  ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-4 py-3 font-mono text-sm text-white/50">
                                  {(t.fees || 0) > 0 ? `$${t.fees.toFixed(2)}` : "—"}
                                </td>
                                <td className="px-4 py-3 text-sm text-white/50 max-w-[150px] truncate">
                                  {t.notes || "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => openEditModal(t)}
                                      data-testid={`edit-btn-${t.id}`}
                                      className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                                      title="Edit"
                                    >
                                      <PencilSimple size={16} />
                                    </button>
                                    
                                    {deletingId === t.id ? (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => handleDelete(t.id)}
                                          className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                                        >
                                          Confirm
                                        </button>
                                        <button
                                          onClick={() => setDeletingId(null)}
                                          className="px-2 py-1 text-xs bg-white/10 text-white/60 rounded hover:bg-white/20"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => setDeletingId(t.id)}
                                        data-testid={`delete-btn-${t.id}`}
                                        className="p-1.5 rounded hover:bg-red-500/20 text-white/50 hover:text-red-400 transition-colors"
                                        title="Delete"
                                      >
                                        <Trash size={16} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Transaction Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-md"
            data-testid="transaction-modal"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-bold font-heading">
                {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Transaction Type Toggle */}
              <div>
                <label className="text-white/60 text-xs font-mono mb-1.5 block">
                  TRANSACTION TYPE *
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, transaction_type: 'buy' })}
                    className={`flex-1 py-2 px-4 rounded-sm font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
                      formData.transaction_type === 'buy'
                        ? 'bg-[#00E676] text-black'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                    data-testid="type-buy"
                  >
                    <ArrowDown size={16} weight="bold" />
                    BUY
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // When switching to sell, pre-select first sellable asset
                      if (sellableAssets.length > 0) {
                        setFormData({ 
                          ...formData, 
                          transaction_type: 'sell',
                          asset: sellableAssets[0].asset
                        });
                      } else {
                        setFormData({ ...formData, transaction_type: 'sell', asset: '' });
                      }
                    }}
                    className={`flex-1 py-2 px-4 rounded-sm font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
                      formData.transaction_type === 'sell'
                        ? 'bg-[#FF5722] text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                    data-testid="type-sell"
                  >
                    <ArrowUp size={16} weight="bold" />
                    SELL
                  </button>
                </div>
              </div>

              {/* No assets to sell warning */}
              {formData.transaction_type === 'sell' && sellableAssets.length === 0 && (
                <div className="bg-[#FF5722]/10 border border-[#FF5722]/30 rounded-sm p-4 text-center">
                  <p className="text-[#FF5722] font-medium">
                    You do not have any holdings available to sell.
                  </p>
                  <p className="text-white/50 text-sm mt-1">
                    Add a BUY transaction first to build your holdings.
                  </p>
                </div>
              )}

              {/* Asset - Different UI for BUY vs SELL */}
              {(formData.transaction_type === 'buy' || sellableAssets.length > 0) && (
                <div>
                  <label className="text-white/60 text-xs font-mono mb-1.5 block">
                    ASSET SYMBOL *
                  </label>
                  
                  {/* BUY: Free text input */}
                  {formData.transaction_type === 'buy' && (
                    <Input
                      type="text"
                      placeholder="e.g., BTC, ETH, SOL"
                      value={formData.asset}
                      onChange={(e) => setFormData({ ...formData, asset: e.target.value.toUpperCase() })}
                      className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                      data-testid="input-asset"
                      autoFocus
                    />
                  )}
                  
                  {/* SELL: Dropdown of sellable assets */}
                  {formData.transaction_type === 'sell' && (
                    <>
                      <select
                        value={formData.asset}
                        onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-white/10 text-white font-mono rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]/50"
                        data-testid="select-asset"
                      >
                        {sellableAssets.map((asset) => (
                          <option key={asset.asset} value={asset.asset}>
                            {asset.asset} — {asset.current_quantity.toLocaleString(undefined, { maximumFractionDigits: 8 })} available
                          </option>
                        ))}
                      </select>
                      
                      {/* Asset details for sell */}
                      {selectedSellAsset && (
                        <div className="mt-3 bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/60 text-xs font-mono">SELECTED ASSET</span>
                            <span className="font-bold text-lg">{selectedSellAsset.asset}</span>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-sm">
                              <span className="text-white/50">Available to sell:</span>
                              <span className="font-mono text-[#00E676]">
                                {selectedSellAsset.current_quantity.toLocaleString(undefined, { maximumFractionDigits: 8 })} {selectedSellAsset.asset}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/50">Avg hold price:</span>
                              <span className="font-mono text-[#FFC107]">
                                ${selectedSellAsset.average_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/50">Cost basis:</span>
                              <span className="font-mono text-white/70">
                                ${selectedSellAsset.current_cost_basis.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                          {/* Max sell button */}
                          <button
                            type="button"
                            onClick={() => setFormData({ 
                              ...formData, 
                              quantity: selectedSellAsset.current_quantity.toString() 
                            })}
                            className="mt-2 w-full text-xs text-[#FF5722] hover:text-[#FF5722]/80 font-mono py-1 border border-[#FF5722]/30 rounded-sm hover:bg-[#FF5722]/10 transition-colors"
                          >
                            SELL MAX ({selectedSellAsset.current_quantity.toLocaleString(undefined, { maximumFractionDigits: 8 })} {selectedSellAsset.asset})
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Transaction Date */}
              <div>
                <label className="text-white/60 text-xs font-mono mb-1.5 block">
                  TRANSACTION DATE *
                </label>
                <Input
                  type="date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                  data-testid="input-date"
                />
              </div>

              {/* Quantity and Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-xs font-mono mb-1.5 block">
                    QUANTITY *
                  </label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                    data-testid="input-quantity"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-mono mb-1.5 block">
                    PRICE PER UNIT *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      placeholder="0.00"
                      value={formData.price_per_unit}
                      onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white font-mono pl-7"
                      data-testid="input-price"
                    />
                  </div>
                </div>
              </div>

              {/* Fees */}
              <div>
                <label className="text-white/60 text-xs font-mono mb-1.5 block">
                  FEES (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    value={formData.fees}
                    onChange={(e) => setFormData({ ...formData, fees: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white font-mono pl-7"
                    data-testid="input-fees"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-white/60 text-xs font-mono mb-1.5 block">
                  NOTES (optional)
                </label>
                <Textarea
                  placeholder="Add any notes about this transaction..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white resize-none h-16"
                  data-testid="input-notes"
                />
              </div>

              {/* Cash Options */}
              {formData.transaction_type === 'sell' && (
                <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.add_proceeds_to_cash}
                      onChange={(e) => setFormData({ ...formData, add_proceeds_to_cash: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-transparent"
                    />
                    <span className="text-sm text-white/70">Add sale proceeds to available cash</span>
                  </label>
                </div>
              )}

              {formData.transaction_type === 'buy' && (
                <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.deduct_from_cash}
                      onChange={(e) => setFormData({ ...formData, deduct_from_cash: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-transparent"
                    />
                    <span className="text-sm text-white/70">Deduct from available cash</span>
                  </label>
                  {formData.deduct_from_cash && (
                    <p className="text-xs text-white/40 mt-1 ml-6">
                      Available: ${availableCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
              )}

              {/* Preview */}
              {formData.quantity && formData.price_per_unit && (
                <div className={`border rounded-sm p-3 ${
                  formData.transaction_type === 'buy' ? 'bg-[#00E676]/5 border-[#00E676]/20' : 'bg-[#FF5722]/5 border-[#FF5722]/20'
                }`}>
                  <div className="text-white/40 text-xs font-mono mb-2">PREVIEW</div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Total Value:</span>
                    <span className={`font-mono font-semibold ${
                      formData.transaction_type === 'buy' ? 'text-[#00E676]' : 'text-[#FF5722]'
                    }`}>
                      ${((parseFloat(formData.quantity) || 0) * (parseFloat(formData.price_per_unit) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  {parseFloat(formData.fees) > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-white/60">+ Fees:</span>
                      <span className="font-mono text-white/60">
                        ${parseFloat(formData.fees).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1 bg-transparent border-white/10 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || (formData.transaction_type === 'sell' && sellableAssets.length === 0)}
                  data-testid="submit-transaction"
                  className={`flex-1 font-bold ${
                    formData.transaction_type === 'buy'
                      ? 'bg-[#00E676] text-black hover:bg-[#00E676]/90'
                      : 'bg-[#FF5722] text-white hover:bg-[#FF5722]/90'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {submitting ? "Saving..." : (editingTransaction ? "Update" : (formData.transaction_type === 'buy' ? "Add Buy" : "Add Sell"))}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cash Balance Modal */}
      {showCashModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-sm"
            data-testid="cash-modal"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-bold font-heading flex items-center gap-2">
                <Bank size={20} weight="duotone" className="text-[#FFC107]" />
                Available Cash
              </h3>
              <button
                onClick={closeCashModal}
                className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <p className="text-white/60 text-sm">
                Set your available cash balance for future asset purchases. This is separate from your trading capital.
              </p>
              
              <div>
                <label className="text-white/60 text-xs font-mono mb-1.5 block">
                  CASH BALANCE
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    value={cashInput}
                    onChange={(e) => setCashInput(e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white font-mono pl-7 text-lg"
                    data-testid="input-cash"
                    autoFocus
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeCashModal}
                  className="flex-1 bg-transparent border-white/10 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveCash}
                  disabled={savingCash}
                  data-testid="submit-cash"
                  className="flex-1 bg-[#FFC107] text-black font-bold hover:bg-[#FFC107]/90"
                >
                  {savingCash ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
